import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Loader,
  Modal,
  Select,
  Text,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { notifications } from "@mantine/notifications";
import { successNotification } from "@/shared/utils/successNotification";

export type SchemeItem = {
  scheme_bse_code: string;
  name: string;
  scheme_amc_name?: string | null;
};

type Props = {
  opened: boolean;
  term: string;
  termLabel: string;
  currentBseCodes: string[];
  onClose: () => void;
  onSaved: (term: string, schemes: SchemeItem[]) => void;
};

export function UpdateSchemesModal({
  opened,
  term,
  termLabel,
  currentBseCodes,
  onClose,
  onSaved,
}: Props) {
  const [availableSchemes, setAvailableSchemes] = useState<SchemeItem[]>([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened || !term) return;
    setSelected(currentBseCodes);

    const fetchSchemes = async () => {
      setLoadingSchemes(true);
      try {
        const res = await trpc.schemes.listByTerm.query({ term });
        setAvailableSchemes(res.data);
      } catch (err) {
        handleTrpcError(err);
      } finally {
        setLoadingSchemes(false);
      }
    };
    fetchSchemes();
  }, [opened, term]);

  const schemeMap = Object.fromEntries(
    availableSchemes.map((s) => [s.scheme_bse_code, s])
  );

  // Only show unselected schemes in the dropdown
  const dropdownData = availableSchemes
    .filter((s) => !selected.includes(s.scheme_bse_code))
    .map((s) => ({ value: s.scheme_bse_code, label: s.name }));

  const handleAdd = (value: string | null) => {
    if (!value || selected.length >= 5) return;
    setSelected((prev) => [...prev, value]);
  };

  const handleRemove = (code: string) => {
    setSelected((prev) => prev.filter((c) => c !== code));
  };

  const handleSave = async () => {
    if (selected.length !== 5) {
      notifications.show({ message: "Please select exactly 5 schemes.", color: "red" });
      return;
    }
    setSaving(true);
    try {
      await trpc.schemes.updateSuggested.mutate({ category: term, schemeBseCodes: selected });
      const saved = selected.map((code) => ({
        scheme_bse_code: code,
        name: schemeMap[code]?.name ?? code,
        scheme_amc_name: schemeMap[code]?.scheme_amc_name ?? null,
      }));
      onSaved(term, saved);
      successNotification({ title: "Schemes Updated", message: `${termLabel} schemes updated.` });
      onClose();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} fz="md">Select schemes for {termLabel}</Text>}
      size="lg"
    >
      <Text fz="sm" c="slate.5" mb="md">
        Select 5 schemes
      </Text>

      {loadingSchemes ? (
        <Center py="xl">
          <Loader size="sm" type="dots" />
        </Center>
      ) : (
        <>
          <Select
            label="Select Schemes"
            placeholder="Select"
            data={dropdownData}
            value={null}
            onChange={handleAdd}
            searchable
            disabled={selected.length >= 5}
            mb="sm"
          />

          {selected.length > 0 && (
            <Box mt="xs">
              <Flex wrap="wrap" gap="xs">
                {selected.map((code) => (
                  <Badge
                    key={code}
                    variant="outline"
                    color="slate"
                    size="lg"
                    radius="sm"
                    style={{ textTransform: "none", fontWeight: 400, maxWidth: "100%" }}
                    rightSection={
                      <ActionIcon
                        size="xs"
                        variant="transparent"
                        color="slate"
                        onClick={() => handleRemove(code)}
                      >
                        <IconX size={10} />
                      </ActionIcon>
                    }
                  >
                    {schemeMap[code]?.name ?? code}
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}
        </>
      )}

      <Flex justify="flex-end" mt="xl">
        <Button
          color="primary"
          onClick={handleSave}
          loading={saving}
          disabled={selected.length !== 5 || loadingSchemes}
        >
          Update schemes
        </Button>
      </Flex>
    </Modal>
  );
}
